/**
 * Dev-only override store (AUTH-122).
 *
 * The DevToolbar drives this store; pages read it but only honor it when
 * `import.meta.env.DEV` is true. This lets us preview every panel of
 * /security (and a few other complex screens) without juggling real auth
 * state, fresh sessions, or valid TOTP codes.
 *
 * Production never reads from here — every consumer guards behind
 * `import.meta.env.DEV`. The store still ships in prod (it's tiny and
 * tree-shaking through React context is unreliable) but is inert.
 */
import { Store } from "@tanstack/store";
import { useStore } from "@tanstack/react-store";

/** Mirrors the security page's TwoFactorStep union. */
export type SecurityStepOverride =
  | "idle"
  | "enabling"
  | "verify"
  | "backup-codes"
  | "disabling"
  | "regenerating-codes"
  | "regenerated-codes";

export interface DevOverrideState {
  /** When set on /security, pins the page's twoFactorStep to this value. */
  securityStep: SecurityStepOverride | null;
  /**
   * When true, the security page renders its panels without waiting for
   * `useAuth().user` to resolve — useful for previewing the page when
   * unauthenticated locally.
   */
  bypassAuthGate: boolean;
}

const initialState: DevOverrideState = {
  securityStep: null,
  bypassAuthGate: false,
};

export const devOverrideStore = new Store<DevOverrideState>(initialState);

export function setSecurityStepOverride(step: SecurityStepOverride | null): void {
  devOverrideStore.setState((s) => ({ ...s, securityStep: step }));
}

export function setBypassAuthGate(bypass: boolean): void {
  devOverrideStore.setState((s) => ({ ...s, bypassAuthGate: bypass }));
}

export function resetDevOverrides(): void {
  devOverrideStore.setState(() => initialState);
}

export function useDevOverride<T>(selector: (s: DevOverrideState) => T): T {
  return useStore(devOverrideStore, selector);
}
